"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLenderApprovalProgressAggregator = exports.createLenderVerificationProgressAggregator = exports.createLenderVerificationUploadProgressAggregator = exports.createDataTapeUploadProgressAggregator = exports.createGenericSequencePoint = exports.createProgressAggregator = void 0;
const lodash_1 = __importDefault(require("lodash"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const index_1 = require("./index");
const xlsx_1 = require("./xlsx");
// exported for testing
/**
 * @param graph
 * @param ignoredSequencePoints sequence points that this aggregator will ignore as tracked
 *   elsewhere
 * @param sequencePoints sequence points that this aggregator tracks and reports progress on. Order
 *   matters: sequence points reached earlier in the pipeline should come earlier in the list.
 */
const createProgressAggregator = ({ graph, ignoredSequencePoints, sequencePoints, }) => {
    // Track the set of artifact ids that have been "captured" (accounted for) by a sequence point.
    // To start, treat `ignoredSequencePoints` as captured by another ProgressAggregator. This
    // prevents them getting captured by any of our own sequence points.
    const allCapturedArtifactIds = new Set();
    index_1.BbplGraph.findDependenciesRecursively(graph, ignoredSequencePoints.flatMap((sequencePoint) => sequencePoint.getGoalArtifactIds())).forEach((ignored) => allCapturedArtifactIds.add(ignored.id));
    const sequencePointInfos = sequencePoints.map((sequencePoint) => {
        const capturedArtifactIds = [];
        index_1.BbplGraph.findDependenciesRecursively(graph, sequencePoint.getGoalArtifactIds()).forEach((dependency) => {
            // Can only capture if no earlier sequence point has captured this artifact yet.
            if (!allCapturedArtifactIds.has(dependency.id)) {
                capturedArtifactIds.push(dependency.id);
                allCapturedArtifactIds.add(dependency.id);
            }
        });
        return { sequencePoint, capturedArtifactIds };
    });
    // The last sequence point is considered to be the overall goal.
    const goalIds = lodash_1.default.last(sequencePoints)?.getGoalArtifactIds() ?? [];
    const dependencyIds = index_1.BbplGraph.findDependenciesRecursively(graph, goalIds).map(({ id }) => id);
    // TODO(jon): isRunning behavior can be improved. We currently return isRunning=false as soon as
    //  we see an error. The client will stop polling and may miss out on other errors associated with
    //  operations that are not yet complete.
    const getPipelineStatus = (artifactInstances) => {
        const isOverallSuccess = goalIds.every((goalId) => artifactInstances.find(({ artifactId }) => artifactId === goalId)
            ?.status === 'success');
        if (isOverallSuccess) {
            return 'success';
        }
        const hasErrorInDependencies = dependencyIds.some((depId) => artifactInstances.find(({ artifactId }) => artifactId === depId)
            ?.status === 'error');
        return hasErrorInDependencies ? 'error' : 'inProgress';
    };
    return {
        getGoalArtifactIds: () => goalIds,
        getProgressSummary: (artifactInstances) => {
            const pipelineStatus = getPipelineStatus(artifactInstances);
            const isRunning = pipelineStatus === 'inProgress';
            const progressBreakdown = [];
            const instancesMap = new Map(artifactInstances.map((instance) => [instance.artifactId, instance]));
            sequencePointInfos.forEach(({ sequencePoint, capturedArtifactIds }) => {
                const capturedArtifactInstances = [];
                capturedArtifactIds.forEach((artifactId) => {
                    const artifactInstance = instancesMap.get(artifactId);
                    if (artifactInstance) {
                        capturedArtifactInstances.push(artifactInstance);
                    }
                });
                const newProgressBreakdown = sequencePoint.getProgressBreakdown(capturedArtifactInstances);
                if (isRunning &&
                    progressBreakdown.every(({ status }) => status.status === 'success') &&
                    newProgressBreakdown.every(({ status }) => status.status === 'notStarted')) {
                    // There is a time gap between the previous artifact instance reporting itself done and
                    // the next artifact instance reporting itself as in progress. To provide a better UX we
                    // can fake an 'inProgress' for the next node if we know the pipeline is running.
                    if (newProgressBreakdown.length) {
                        newProgressBreakdown[0].status.status = 'inProgress';
                    }
                }
                progressBreakdown.push(...newProgressBreakdown);
            });
            // It's possible for a later sequence point to capture artifacts that start executing
            // prior to an earlier sequence point fully completing. This can cause a strange UX where a
            // later step shows as in-progress while an earlier step is errored out or still in progress.
            //
            // Workaround to avoid this: mark all steps as not started unless all steps prior are
            // successful.
            let allStepsCompleteSoFar = ignoredSequencePoints
                .flatMap((isp) => isp.getGoalArtifactIds())
                .every((ignoredGoalId) => artifactInstances.find(({ artifactId }) => artifactId === ignoredGoalId)?.status === 'success');
            progressBreakdown.forEach((step) => {
                const isStepComplete = step.status.status === 'success';
                if (!allStepsCompleteSoFar) {
                    // eslint-disable-next-line no-param-reassign
                    step.status = { status: 'notStarted' };
                }
                allStepsCompleteSoFar = allStepsCompleteSoFar && isStepComplete;
            });
            return { isRunning, status: pipelineStatus, progressBreakdown };
        },
    };
};
exports.createProgressAggregator = createProgressAggregator;
/**
 * Error message for when no publicly relevant details are available, and the customer can't take
 * action themselves.
 */
const genericErrorMessage = 'An error occurred, and our team has been notified. Please contact your Finley representative for details.';
const toGenericAggregatedBreakdown = (progressBreakdown) => progressBreakdown.map((step) => {
    switch (step.status.status) {
        case 'inProgress':
        case 'success':
        case 'notStarted':
            return {
                stepDescription: step.stepDescription,
                status: { status: step.status.status },
            };
        case 'error': {
            return {
                stepDescription: step.stepDescription,
                status: {
                    status: 'error',
                    errors: [{ type: 'message', message: genericErrorMessage }],
                },
            };
        }
        default:
            throw (0, common_1.finleyAssertUnreachable)(step.status);
    }
});
const createProgressBreakdown = (stepDescriptions, status) => {
    return stepDescriptions.map((stepDescription) => {
        return {
            stepDescription,
            status: status === 'error'
                ? {
                    status,
                    errors: [{ type: 'message', message: genericErrorMessage }],
                }
                : { status },
        };
    });
};
/** Null return means we have no interesting information to report, not that there is no error. */
const toDataValidationError = (fieldGroup, errorInstances) => {
    const xlsxErrorInstance = fieldGroup.xlsx2CsvArtifact &&
        errorInstances.find(({ artifactId }) => artifactId === fieldGroup.xlsx2CsvArtifact.id);
    const sanitizeErrorInstance = errorInstances.find(({ artifactId }) => artifactId === fieldGroup.sanitizeArtifact.id);
    const dataValidationError = {
        type: 'dataValidation',
        dataTapeSlug: fieldGroup.dataTapeSlug,
        xlsxExtractionErrors: [],
        xlsxCellErrors: [],
        xlsxColumnErrors: [],
        csvFieldErrors: [],
        csvColumnErrors: [],
    };
    if (xlsxErrorInstance) {
        xlsxErrorInstance.statusDetails.errors.forEach((error) => fieldGroup.collectErrors(error, dataValidationError));
    }
    if (sanitizeErrorInstance) {
        sanitizeErrorInstance.statusDetails.errors.forEach((error) => fieldGroup.collectErrors(error, dataValidationError));
    }
    if (dataValidationError.xlsxExtractionErrors.length ||
        dataValidationError.xlsxCellErrors.length ||
        dataValidationError.xlsxColumnErrors.length ||
        dataValidationError.csvFieldErrors.length ||
        dataValidationError.csvColumnErrors.length) {
        return dataValidationError;
    }
    return null;
};
const createDataValidationSequencePointForRawTape = (graph, rawArtifactId) => {
    (0, common_1.finleyAssert)(graph.artifacts.some(({ id }) => id === rawArtifactId), `createDataValidationSequencePointForRawTape: could not find [artifactId: ${rawArtifactId}]`);
    // Try treating the raw tape as both an xlsx and a csv. Only one of these getters will return
    // results.
    const fieldGroups = [
        ...xlsx_1.XlsxExtraction.getXlsxFieldGroups(graph, rawArtifactId),
        ...xlsx_1.XlsxExtraction.getCsvFieldGroups(graph, rawArtifactId),
    ];
    const goalArtifactIds = lodash_1.default.chain(fieldGroups)
        .flatMap(({ xlsx2CsvArtifact, sanitizeArtifact }) => {
        return [xlsx2CsvArtifact?.id, sanitizeArtifact.id];
    })
        .uniq()
        .compact()
        .value();
    return {
        getGoalArtifactIds: () => goalArtifactIds,
        getProgressBreakdown: (artifactInstances) => {
            const stepDescription = 'Validate input data';
            let sequencePointStatus;
            if (artifactInstances.length === 0) {
                sequencePointStatus = 'notStarted';
            }
            else if (artifactInstances.some(({ status }) => status === 'error')) {
                const errorInstances = [];
                artifactInstances.forEach((instance) => {
                    if (instance.status === 'error')
                        errorInstances.push(instance);
                });
                const fieldGroupErrors = fieldGroups
                    .map((fieldGroup) => toDataValidationError(fieldGroup, errorInstances))
                    .filter(common_1.typeguards.isNotNullOrUndefined);
                return [
                    {
                        stepDescription,
                        status: { status: 'error', errors: fieldGroupErrors },
                    },
                ];
            }
            else if (goalArtifactIds.every((goalId) => artifactInstances.find(({ artifactId }) => artifactId === goalId)
                ?.status === 'success')) {
                sequencePointStatus = 'success';
            }
            else {
                sequencePointStatus = 'inProgress';
            }
            return createProgressBreakdown([stepDescription], sequencePointStatus);
        },
    };
};
const createDataValidationSequencePointForFundingVerification = (graph) => {
    const rawLoanTapeArtifacts = graph.artifacts.filter(({ type }) => type === 'rawLoanTape');
    (0, common_1.finleyAssert)(rawLoanTapeArtifacts.length === 1, 'Expected exactly one rawLoanTape');
    return createDataValidationSequencePointForRawTape(graph, rawLoanTapeArtifacts[0].id);
};
const createCalcAgentSequencePoint = (graph) => {
    const calcAgentArtifacts = lodash_1.default.chain(graph.artifacts)
        .filter(({ type }) => type === 'receivableResponseTape')
        .sortBy(({ artifactSlug }) => artifactSlug) // give them a stable order
        .value();
    return {
        getGoalArtifactIds: () => calcAgentArtifacts.map(({ id }) => id),
        getProgressBreakdown: (artifactInstances) => 
        // In practice, there shouldn't be more than one calc agent node in a graph. Handling of
        // multiple nodes is just here for completeness.
        calcAgentArtifacts.flatMap((artifact) => {
            const instance = artifactInstances.find(({ artifactId }) => artifactId === artifact.id);
            switch (instance?.status) {
                case 'inProgress':
                case 'error':
                case 'success':
                    if (instance.statusDetails.progressBreakdown) {
                        return toGenericAggregatedBreakdown(instance.statusDetails.progressBreakdown);
                    }
                    return createProgressBreakdown(finley_types_1.Pipeline.CalcAgent.STEP_DESCRIPTIONS, instance.status);
                default:
                    return createProgressBreakdown(finley_types_1.Pipeline.CalcAgent.STEP_DESCRIPTIONS, 'notStarted');
            }
        }),
    };
};
// exported for testing
const createGenericSequencePoint = (stepDescriptions, goalArtifactPredicate, graph) => {
    const goalIds = graph.artifacts
        .filter(goalArtifactPredicate)
        .map(({ id }) => id);
    return {
        getGoalArtifactIds: () => goalIds,
        getProgressBreakdown: (artifactInstances) => {
            let sequencePointStatus;
            if (artifactInstances.length === 0) {
                sequencePointStatus = 'notStarted';
            }
            else if (artifactInstances.some(({ status }) => status === 'error')) {
                sequencePointStatus = 'error';
            }
            else if (goalIds.every((goalId) => artifactInstances.find(({ artifactId }) => artifactId === goalId)
                ?.status === 'success')) {
                sequencePointStatus = 'success';
            }
            else {
                sequencePointStatus = 'inProgress';
            }
            return createProgressBreakdown(stepDescriptions, sequencePointStatus);
        },
    };
};
exports.createGenericSequencePoint = createGenericSequencePoint;
const createGenerateLoanTapesSequencePointForFundingVerification = (graph) => (0, exports.createGenericSequencePoint)(finley_types_1.Pipeline.GenerateLoanTapes.STEP_DESCRIPTIONS, 
// For funding verification, `assignedLoanTape` corresponds with what we surface to the user as
// "Generate loan tapes". It's the step directly prior to running calc agent.
({ type }) => type === 'assignedLoanTape', graph);
const createFundingVerificationSequencePoint = (graph) => (0, exports.createGenericSequencePoint)(finley_types_1.Pipeline.FundingVerificationReport.STEP_DESCRIPTIONS, ({ type }) => type === 'fundingVerificationReport', graph);
const createFundingApprovalSequencePoint = (graph) => (0, exports.createGenericSequencePoint)(finley_types_1.Pipeline.FundingVerificationApproval.STEP_DESCRIPTIONS, ({ type }) => type === 'fundingRequest', graph);
const createDataTapeUploadProgressAggregator = (graph, rawTapeArtifactId) => (0, exports.createProgressAggregator)({
    graph,
    ignoredSequencePoints: [],
    sequencePoints: [
        createDataValidationSequencePointForRawTape(graph, rawTapeArtifactId),
    ],
});
exports.createDataTapeUploadProgressAggregator = createDataTapeUploadProgressAggregator;
const createLenderVerificationUploadProgressAggregator = (graph) => (0, exports.createProgressAggregator)({
    graph,
    ignoredSequencePoints: [],
    sequencePoints: [
        createDataValidationSequencePointForFundingVerification(graph),
    ],
});
exports.createLenderVerificationUploadProgressAggregator = createLenderVerificationUploadProgressAggregator;
const createLenderVerificationProgressAggregator = (graph) => (0, exports.createProgressAggregator)({
    graph,
    ignoredSequencePoints: [
        createDataValidationSequencePointForFundingVerification(graph),
    ],
    sequencePoints: [
        createGenerateLoanTapesSequencePointForFundingVerification(graph),
        createCalcAgentSequencePoint(graph),
        createFundingVerificationSequencePoint(graph),
    ],
});
exports.createLenderVerificationProgressAggregator = createLenderVerificationProgressAggregator;
const createLenderApprovalProgressAggregator = (graph) => (0, exports.createProgressAggregator)({
    graph,
    ignoredSequencePoints: [],
    sequencePoints: [createFundingApprovalSequencePoint(graph)],
});
exports.createLenderApprovalProgressAggregator = createLenderApprovalProgressAggregator;
//# sourceMappingURL=progress.js.map